/**
 * CR140.2: Scheduler Timezone Utilities
 * Provides consistent UTC ↔ ET (America/Toronto) conversion for scheduler UI
 */

const SCHEDULER_TIMEZONE = "America/Toronto";

/**
 * Get short timezone abbreviation (ET for Eastern Time)
 */
export function getTimezoneAbbr(tz: string = SCHEDULER_TIMEZONE): string {
  if (tz === "America/Toronto" || tz === "America/New_York") {
    return "ET";
  }
  // Fallback: extract from full timezone
  return tz.split("/").pop() || tz;
}

/**
 * Format a UTC date string for display in the schedule's timezone
 * Returns formatted string like "Jan 25, 9:00 AM (ET)"
 */
export function formatScheduleTime(
  utcDateString: string | null | undefined,
  options: { includeDate?: boolean; includeTimezone?: boolean } = {}
): string {
  const { includeDate = true, includeTimezone = true } = options;

  if (!utcDateString) return "—";

  try {
    const date = new Date(utcDateString);
    if (isNaN(date.getTime())) return "Invalid date";

    const formatted = date.toLocaleString("en-CA", {
      timeZone: SCHEDULER_TIMEZONE,
      ...(includeDate
        ? { month: "short", day: "numeric" }
        : {}),
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return includeTimezone ? `${formatted} (${getTimezoneAbbr()})` : formatted;
  } catch {
    return "Invalid date";
  }
}

/**
 * Format just the date portion (for tooltips, headers)
 * Returns "Jan 25, 2026"
 */
export function formatScheduleDate(utcDateString: string | null | undefined): string {
  if (!utcDateString) return "—";

  try {
    const date = new Date(utcDateString);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleString("en-CA", {
      timeZone: SCHEDULER_TIMEZONE,
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "Invalid date";
  }
}

/**
 * Get the raw UTC ISO string for debug tooltip
 */
export function getUtcDebugString(utcDateString: string | null | undefined): string {
  if (!utcDateString) return "No timestamp";
  
  try {
    const date = new Date(utcDateString);
    if (isNaN(date.getTime())) return "Invalid date";
    return `Stored as UTC: ${date.toISOString()}`;
  } catch {
    return "Invalid date";
  }
}

/**
 * Parse a UTC timestamp and extract ET date + time components for the editor
 * This prevents "time shifting" when re-opening the editor
 */
export function parseUtcToEtComponents(utcDateString: string): { date: Date; time: string } {
  const utcDate = new Date(utcDateString);
  
  // Get the date/time parts as they appear in ET
  const etParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SCHEDULER_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(utcDate);

  const partsMap: Record<string, string> = {};
  for (const part of etParts) {
    partsMap[part.type] = part.value;
  }

  // Construct a local Date object that represents the ET date visually
  // This is for the Calendar component which shows dates in local time
  const etYear = parseInt(partsMap.year);
  const etMonth = parseInt(partsMap.month) - 1; // 0-indexed
  const etDay = parseInt(partsMap.day);
  
  // Create a Date in local browser time that shows the ET date
  const localDate = new Date(etYear, etMonth, etDay);
  
  // Format time as HH:mm for the time input
  const time = `${partsMap.hour}:${partsMap.minute}`;

  return { date: localDate, time };
}

/**
 * Get timezone offset in minutes for a given timezone at a specific UTC moment.
 * Positive offset means the timezone is ahead of UTC.
 */
function getTzOffsetMinutes(tz: string, utcDate: Date): number {
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const parts = dtf.formatToParts(utcDate);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;

  // Build "as-if UTC" timestamp from the time-zone wall clock values
  const asIfUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );

  // Offset = local wallclock (as-if UTC) - actual UTC
  return Math.round((asIfUtc - utcDate.getTime()) / 60000);
}

/**
 * Convert ET date + time components back to UTC for storage (DST-safe).
 * date: calendar-selected day
 * time: "HH:mm" interpreted in schedule timezone
 */
export function combineEtComponentsToUtc(date: Date, time: string): string {
  const [h, m] = time.split(":").map((n) => Number(n));

  // Treat the calendar day as a pure Y/M/D (avoid browser timezone effects)
  const y = date.getFullYear();
  const mo = date.getMonth();
  const d = date.getDate();

  // Initial guess: interpret that wall time as UTC
  const guessUtcMs = Date.UTC(y, mo, d, h, m, 0, 0);
  const guessUtc = new Date(guessUtcMs);

  // Compute timezone offset at that moment and adjust
  const offset1 = getTzOffsetMinutes(SCHEDULER_TIMEZONE, guessUtc);
  const adjustedUtcMs = guessUtcMs - offset1 * 60000;
  const adjustedUtc = new Date(adjustedUtcMs);

  // Second pass to handle DST boundaries precisely
  const offset2 = getTzOffsetMinutes(SCHEDULER_TIMEZONE, adjustedUtc);
  const finalUtcMs = guessUtcMs - offset2 * 60000;

  return new Date(finalUtcMs).toISOString();
}

/**
 * Get full timezone label for display
 */
export function getTimezoneLabel(tz: string = SCHEDULER_TIMEZONE): string {
  const abbr = getTimezoneAbbr(tz);
  return `${abbr} (${tz})`;
}
